import React, { useMemo } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import type { HotelRoomBlockPricingValues, StandardHotelPricingValues, AccommodationsConsoleState } from '@shared/types/Accommodations.types';
import { Skeleton } from '@shared/components/Skeleton';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import { DiscountBadge, DiscountBadgeWithTooltip } from './DiscountBadge';
import { useAccommodationsListingMobileLayout } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsListingMobileLayout';

type AccommodationsDiscountTooltipPayload = Readonly<{
  minimumDiscountAmountToShow: number;
}>;

const isAccommodationsDiscountTooltipPayload = (value: unknown): value is AccommodationsDiscountTooltipPayload => {
  if (!value || typeof value !== 'object') return false;
  const payload = value as { minimumDiscountAmountToShow?: unknown };
  return typeof payload.minimumDiscountAmountToShow === 'number';
};

const CostBreakdown = ({
  pricingInfo,
  noOfNights,
  occupancies,
}: Readonly<{
  pricingInfo: StandardHotelPricingValues | HotelRoomBlockPricingValues;
  occupancies: AccommodationsConsoleState['occupancies'];
  noOfNights: number;
}>) => {
  const { isMobileOrTablet } = useAccommodationsListingMobileLayout();
  const { discountPercentage, nightlyBaseRateString, strikeoutNightlyBaseRateString, totalRateString, discountAmountString, discountAmount } = pricingInfo;

  const { tq } = useTranslation('accommodationsConsole');
  const { payload: accommodationsDiscountTooltipPayload } = useFeatureValue('accommodationsDiscountTooltipEnabled');
  const minimumDiscountAmountToShow = isAccommodationsDiscountTooltipPayload(accommodationsDiscountTooltipPayload)
    ? accommodationsDiscountTooltipPayload.minimumDiscountAmountToShow
    : 0;

  const occupancyText = useMemo(() => {
    if (noOfNights === 1 && occupancies.unitCount === 1) {
      return tq`1 night, 1 room, incl. taxes and fees`;
    }
    if (noOfNights === 1 && (occupancies.unitCount || 0) > 1) {
      return tq`1 night, ${occupancies.unitCount} rooms, incl. taxes and fees`;
    }
    if (noOfNights > 1 && occupancies.unitCount === 1) {
      return tq`${noOfNights} nights, 1 room, incl. taxes and fees`;
    }
    return tq`${noOfNights} nights, ${occupancies.unitCount} rooms, incl. taxes and fees`;
  }, [tq, noOfNights, occupancies.unitCount]);

  const hasDiscountStrings = !!(strikeoutNightlyBaseRateString && discountAmountString);
  const hasTooltipData = hasDiscountStrings && accommodationsDiscountTooltipPayload && discountAmount && discountAmount > minimumDiscountAmountToShow;

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-end"
    >
      {discountPercentage &&
        (hasTooltipData ? (
          <DiscountBadgeWithTooltip
            totalPrice={nightlyBaseRateString}
            discountAmount={discountAmountString}
            label={tq`${discountPercentage}% off`}
            originalPrice={strikeoutNightlyBaseRateString}
          />
        ) : (
          <DiscountBadge label={tq`${discountPercentage}% off`} />
        ))}
      <Flex
        columnGap={1}
        alignItems="center"
        marginTop={discountPercentage ? 2 : 0}
      >
        {strikeoutNightlyBaseRateString && (
          <TextV2
            textDecoration="line-through"
            color="mono10"
            typographyVariant="label2"
          >
            {strikeoutNightlyBaseRateString}
          </TextV2>
        )}
        <TextV2
          typographyVariant={isMobileOrTablet ? 'hed1' : 'hed6'}
          fontWeight={600}
        >
          {nightlyBaseRateString}
        </TextV2>
        <TextV2
          typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}
          color="mono10"
          fontWeight={400}
        >
          {tq`/ night`}
        </TextV2>
      </Flex>
      {totalRateString && (
        <>
          <TextV2
            marginTop={1}
            textAlign="end"
            typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}
            color="mono10"
          >
            {tq`${totalRateString} total`}
          </TextV2>
          <TextV2
            marginTop={1}
            textAlign="end"
            typographyVariant="label1"
            color="mono10"
          >
            {occupancyText}
          </TextV2>
        </>
      )}
    </Flex>
  );
};

CostBreakdown.Skeleton = () => (
  <Flex
    rowGap="10px"
    alignItems="flex-end"
    flexDirection="column"
  >
    <Skeleton
      height={28}
      width={105}
    />
    <Skeleton
      height={16}
      width={80}
    />
    <Skeleton
      height={12}
      width={210}
    />
    <Flex
      width="100%"
      marginTop={5}
      justifyContent="center"
    >
      <Skeleton
        height={30}
        width={{ _: '100%', md: 200 }}
      />
    </Flex>
  </Flex>
);

export { CostBreakdown };
